<template>
  <div class="device-terminal-page">
    <div v-if="device != null" class="top-line--flex">
      <div class="device-terminal-page__content">
        <div class="device-terminal-page__wrapper-title">
          <i class="main" style="margin-top: 23px">input</i>
          <h1 v-if="device" class="model-text-wrapper">
            {{ device.name }}
          </h1>

          <div class="pt-0 pb-2">
            <template v-if="device.name !== ' '">
              <DeviceType :model="device.model" class="mr-2" />
              <DeviceConnection :device="device" class="mr-2" />
              <StatusOnline
                :isOnline="device.is_online"
                :wasOnline="device.last_online_at"
                class="mr-2"
              />
            </template>
          </div>
        </div>

        <div v-if="device.is_online" class="device-terminal-page__wrapper-section">
          <label class="block" style="margin-bottom: 3px">Actions</label>
          <Btn
            height="s"
            type="primary"
            icon-left="send"
            :isLoading="isRequestingDompHistory"
            @click="requestDompHistory"
          >
            Save Domp History
          </Btn>
        </div>

        <template v-if="device.is_online">
          <div
            class="device-terminal-page__wrapper-section device-terminal-page__wrapper-section--select-serial-device"
          >
            <label class="block" style="margin-bottom: 3px">Serial Device</label>
            <select v-model="serialDeviceId" required name="position_id">
              <option
                v-for="serialDevice in serialDevices"
                :key="serialDevice.serial"
                :value="serialDevice.id"
              >
                {{ serialDevice.name }} - {{ serialDevice.serial }} - {{ serialDevice.id }}
              </option>
            </select>
          </div>

          <DompBrowser
            v-if="serialDeviceId"
            :key="serialDeviceId"
            :serialDeviceId="serialDeviceId"
            :deviceSocket="socket"
          />
        </template>
      </div>
    </div>
    <ErrorComponent v-else-if="errorCode != null" :code="errorCode" style="margin-top: 64px" />
  </div>
</template>

<script>
  import 'assets/css/base/icons.scss';

  import DeviceSocket, { DeviceSocketEvents } from 'api/sockets/DeviceSocket';
  import StatusOnline from '@/uikitProject/statuses/StatusOnline.vue';
  import Btn from '@/uikitBase/btns/Btn';
  import PageTitle from '@/mixins/page-title';
  import NavigatorHelper from 'utils/NavigatorHelper.ts';
  import DeviceType from '@/uikitProject/devices/DeviceType';
  import DeviceConnection from '@/uikitProject/devices/vueDeviceConnection/DeviceConnection.vue';
  import DompBrowser from 'components/blocks/devices/vueDompBrowser/DompBrowser';
  import ErrorComponent from '@/components/element/ErrorComponent.vue';
  import { apiDevices } from '@/api/graphql/cloud/devices';

  const preventWhiteRectForMobileSafari = () => {
    if (NavigatorHelper.isMobile && NavigatorHelper.browserName === 'safari') {
      setTimeout(() => {
        document.documentElement.scrollTop = 0;
      }, 400);
    }
  };

  export default {
    name: 'DeviceTerminalPage',

    components: {
      ErrorComponent,
      Btn,
      DompBrowser,
      DeviceConnection,
      DeviceType,
      StatusOnline,
    },

    mixins: [PageTitle],

    props: {
      id: String,
    },

    data() {
      return {
        pageTitle: 'HPLC Cloud: Device',

        device: null,
        deviceState: null,

        socket: this.createDeviceSocket(this.id),

        subscriptionForDevice: null,
        subscriptionForDeviceState: null,

        connectionLost: false,
        timerConnectionLost: null,

        errorCode: null,

        serialDeviceId: '',
        serial: '',
        command: '',

        isRequestingDompHistory: false,
        dompHistoryPollTimer: null,
      };
    },

    computed: {
      state() {
        return this.deviceState;
      },
      configuration() {
        return this.device?.configuration;
      },
      serialDevices() {
        return this.device?.configuration?.serial_hardware_config?.serial_devices;
      },
    },

    watch: {
      id: {
        handler(id) {
          this.destroyDeviceSocket();
          this.socket = this.createDeviceSocket(id);

          this.initDeviceData(id);
        },
        immediate: true,
      },
      device(d) {
        if (d?.is_online !== false) {
          this.onConnectionConfirmed();
        }
        this.pageTitle = d?.name || 'HPLC Cloud: Device';
      },
      connectionLost(connectionLost) {
        if (!connectionLost) {
          apiDevices.doNotSleep(this.id);
        }
      },
      state(s) {
        if (s.ready == null) return;
        if (s.sleep_countdown && s.sleep_countdown <= 1) {
          apiDevices.doNotSleep(this.id);
        }
        this.onConnectionConfirmed();
      },
    },

    mounted() {
      preventWhiteRectForMobileSafari();
    },

    beforeDestroy() {
      if (this.timerConnectionLost != null) clearTimeout(this.timerConnectionLost);
      this.destroyDeviceSocket();

      this.stopSubscriptions();
      this.stopPollingDompHistory();
    },

    methods: {
      async requestDompHistory() {
        if (this.isRequestingDompHistory) {
          return;
        }

        this.isRequestingDompHistory = true;
        try {
          await apiDevices.requestDompHistory(this.id);
          this.startPollingDompHistory();
        } catch (e) {
          this.notifyError('Failed to save DOMP logs');
          this.isRequestingDompHistory = false;
          this.stopPollingDompHistory();
        }
      },
      startPollingDompHistory() {
        this.stopPollingDompHistory();

        const poll = async () => {
          try {
            const response = await apiDevices.getDompHistory(this.id);
            const state = response?.domp_history?.state;
            if (state != null && state === 'requested') {
              this.dompHistoryPollTimer = setTimeout(poll, 1000);
              return;
            }

            if (state === 'error') {
              this.notifyError('Failed to save DOMP logs');
              this.isRequestingDompHistory = false;
              this.stopPollingDompHistory();
              return;
            }

            if (state === 'ready') {
              this.notify('Logs saved. Ask the file in the chat');
              this.isRequestingDompHistory = false;
              this.stopPollingDompHistory();
              return;
            }
          } catch (e) {
            this.notifyError('Failed to check DOMP history state');
            this.isRequestingDompHistory = false;
            this.stopPollingDompHistory();
          }
        };

        this.dompHistoryPollTimer = setTimeout(poll, 1000);
      },
      stopPollingDompHistory() {
        if (this.dompHistoryPollTimer != null) {
          clearTimeout(this.dompHistoryPollTimer);
          this.dompHistoryPollTimer = null;
        }
      },
      onConnectionConfirmed() {
        this.connectionLost = false;
        this.updateConnectionLostTimeout();
      },
      updateConnectionLostTimeout() {
        if (this.timerConnectionLost != null) clearTimeout(this.timerConnectionLost);
        this.timerConnectionLost = setTimeout(this.onConnectionLost, 4500);
      },
      onConnectionLost() {
        this.connectionLost = true;

        apiDevices.doNotSleep(this.id);

        this.updateConnectionLostTimeout();
      },

      createDeviceSocket(id) {
        const socket = DeviceSocket.start(
          id,
          () => {
            apiDevices.doNotSleep(this.id);
          },
          (e) => {
            this.errorCode = e.code;
          },
        );

        socket.addEventListener(DeviceSocketEvents.ERROR, (error) => this.notifyError(error));

        return socket;
      },
      destroyDeviceSocket() {
        if (this.socket != null) this.socket.close();
        this.socket = null;
      },

      initDeviceData(deviceId) {
        this.stopSubscriptions();
        this.initSubscriptionForDevice(deviceId);
        this.initSubscriptionForDeviceState(deviceId);
      },
      stopSubscriptions() {
        this.subscriptionForDevice?.unsubscribe();
        this.subscriptionForDeviceState?.unsubscribe();
      },

      async initSubscriptionForDevice(deviceId) {
        this.subscriptionForDevice = await apiDevices.subscribeForDevice(deviceId, (device) => {
          this.device = device;
        });
      },
      async initSubscriptionForDeviceState(deviceId) {
        this.subscriptionForDeviceState = await apiDevices.subscribeForDeviceState(
          deviceId,
          (deviceState) => {
            this.deviceState = deviceState;
          },
        );
      },
    },
  };
</script>

<style scoped lang="scss">
  .device-terminal-page {
    &__content {
      overflow: hidden;
    }

    &__wrapper-title {
      align-items: baseline;
    }

    &__wrapper-section {
      margin-top: 20px;

      &--select-serial-device {
        margin-bottom: 20px;
      }
    }
  }
</style>
